/**
 * DrawerSkeleton
 *
 * Loading skeleton components for different drawer types.
 * Shows while drawer content is loading to prevent layout shift.
 *
 * @see docs/stories/DRAWER-5.5-final-polish-edge-cases.md
 */

import { Skeleton } from "@/components/ui/skeleton";
import type { DrawerType } from "@/types/drawer";

// ============================================================================
// Types
// ============================================================================

interface DrawerSkeletonProps {
  type: DrawerType | "generic";
}

// ============================================================================
// Skeleton Variants
// ============================================================================

function TaskDrawerSkeleton() {
  return (
    <div className="p-6 space-y-4">
      {/* Title field */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-10 w-full" />
      </div>

      {/* Description field */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-24 w-full" />
      </div>

      {/* Assignee and Due Date row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>

      {/* Priority and Status row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </div>
  );
}

function FinanceDrawerSkeleton() {
  return (
    <div className="divide-y">
      {/* System Overview Section */}
      <div className="px-6 py-4">
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Skeleton className="h-3 w-20 mb-1" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div>
            <Skeleton className="h-3 w-20 mb-1" />
            <Skeleton className="h-6 w-20" />
          </div>
        </div>
      </div>

      {/* Financing Section */}
      <div className="px-6 py-4">
        <Skeleton className="h-4 w-24 mb-3" />
        <div className="space-y-3">
          <div>
            <Skeleton className="h-3 w-28 mb-1" />
            <Skeleton className="h-5 w-32" />
          </div>
          <div>
            <Skeleton className="h-3 w-28 mb-1" />
            <Skeleton className="h-5 w-24" />
          </div>
          <div>
            <Skeleton className="h-3 w-28 mb-1" />
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="px-6 py-4 bg-primary/5">
        <Skeleton className="h-4 w-20 mb-3" />
        <div>
          <Skeleton className="h-3 w-36 mb-1" />
          <Skeleton className="h-7 w-28" />
        </div>
      </div>
    </div>
  );
}

function ContactDrawerSkeleton() {
  return (
    <div className="p-6 space-y-4">
      {/* Avatar and name */}
      <div className="flex items-center gap-4">
        <Skeleton className="h-16 w-16 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>

      {/* Contact info */}
      <div className="space-y-3 pt-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>
    </div>
  );
}

function GenericDrawerSkeleton() {
  return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-24 w-full" />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function DrawerSkeleton({ type }: DrawerSkeletonProps) {
  switch (type) {
    case "task":
      return <TaskDrawerSkeleton />;
    case "finance":
      return <FinanceDrawerSkeleton />;
    case "contact":
      return <ContactDrawerSkeleton />;
    default:
      return <GenericDrawerSkeleton />;
  }
}

export default DrawerSkeleton;
